import { useEffect, useRef, useState } from "react";
import { X, Plus, Search, Loader2, Music2, Database, ExternalLink } from "lucide-react";
import { searchArtists, saveArtist } from "@/lib/afmApi";

const SpotifyIcon = () => <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#1DB954] text-[9px] font-black text-black">S</span>;
const AppleIcon = () => <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-black text-[9px] font-black text-white"></span>;

const fmt = (n) => (n == null ? "" : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : `${n}`);

const Row = ({ a, onPick, testId }) => {
  const link = a.spotify_url || a.apple_url;
  return (
  <div className="flex w-full items-center gap-2 pr-2 hover:bg-slate-50">
    <button type="button" data-testid={testId} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(a)} className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left">
      {a.image ? <img src={a.image} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" /> : <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100"><Music2 className="h-4 w-4 text-slate-400" /></div>}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-black">{a.name}</div>
        <div className="truncate text-[11px] text-slate-500">
          {a.followers != null && `${fmt(a.followers)} seguidores`}{a.followers != null && a.genres?.length ? " · " : ""}{a.genres?.join(", ")}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">{a.spotify_id && <SpotifyIcon />}{a.apple_id && <AppleIcon />}</div>
    </button>
    {link && <a href={link} target="_blank" rel="noreferrer" onMouseDown={(e) => e.preventDefault()} data-testid={`${testId}-link`} title="Ver perfil" className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-200 hover:text-black"><ExternalLink className="h-3.5 w-3.5" /></a>}
  </div>
  );
};

const Group = ({ icon: Icon, label, items, onPick, prefix }) => items.length ? (
  <div>
    <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><Icon className="h-3 w-3" /> {label}</div>
    {items.map((a, i) => <Row key={`${prefix}-${i}`} a={a} onPick={onPick} testId={`${prefix}-result-${i}`} />)}
  </div>
) : null;

export function ArtistPicker({ label, req, value = [], onChange, testId, ph = "Buscar artista…", single = false, userId, compact = false }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(6);
  const [res, setRes] = useState({ internal: [], spotify: [], apple: [] });
  const t = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes({ internal: [], spotify: [], apple: [] }); return; }
    clearTimeout(t.current);
    t.current = setTimeout(async () => {
      setLoading(true);
      try { setRes(await searchArtists(q.trim(), limit)); } catch { setRes({ internal: [], spotify: [], apple: [] }); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t.current);
  }, [q, limit]);

  const chosen = new Set(value.map((v) => v.name.toLowerCase()));
  const pick = async (a) => {
    if (chosen.has(a.name.toLowerCase())) { setQ(""); return; }
    let saved = a;
    try { saved = await saveArtist({ name: a.name, spotify_id: a.spotify_id, spotify_url: a.spotify_url, apple_id: a.apple_id, apple_url: a.apple_url, image: a.image, created_by: userId }); } catch {}
    onChange(single ? [saved] : [...value, saved]);
    setQ(""); setOpen(false);
  };
  const remove = (name) => onChange(value.filter((v) => v.name !== name));
  const matchAll = [...res.internal, ...res.spotify, ...res.apple].some((a) => a.name.toLowerCase() === q.trim().toLowerCase());
  const show = open && q.trim().length >= 2;
  const canType = !single || value.length === 0;

  return (
    <div className="relative" data-testid={testId}>
      {label && <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{label} {req && <span className="text-[#AC3333]">*</span>}</label>}
      <div className={`flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 transition-colors focus-within:border-black ${open ? "border-black" : "border-slate-300"}`}>
        {value.map((v) => {
          const link = v.spotify_url || v.apple_url;
          return (
          <span key={v.name} data-testid={`${testId}-chip`} className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-md bg-[#9E1B22] py-1 pl-2 pr-1 text-xs font-semibold text-white">
            {v.image && <img src={v.image} alt="" className="h-4 w-4 shrink-0 rounded-full object-cover" />}
            {link
              ? <a href={link} target="_blank" rel="noreferrer" data-testid={`${testId}-chip-link`} title={`Ver perfil de ${v.name}`} className="truncate underline-offset-2 hover:underline">{v.name}</a>
              : <span className="truncate" title="Artista nuevo (sin perfil vinculado)">{v.name}</span>}
            {v.spotify_id && <SpotifyIcon />}
            <button type="button" onClick={() => remove(v.name)} className="shrink-0 rounded p-0.5 hover:bg-white/20" aria-label="Quitar" data-testid={`${testId}-chip-remove`}><X className="h-3 w-3" /></button>
          </span>
          );
        })}
        {canType && (
          <input data-testid={`${testId}-input`} value={q} onChange={(e) => { setQ(e.target.value); setLimit(6); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { e.preventDefault(); pick({ name: q.trim() }); } if (e.key === "Backspace" && !q && value.length) remove(value[value.length - 1].name); }}
            placeholder={value.length ? "" : ph} className={`min-w-[120px] flex-1 bg-transparent px-1 py-1 outline-none ${compact ? "text-xs" : "text-sm"}`} />
        )}
        {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin text-slate-400" /> : canType && <Search className="mr-1 h-4 w-4 text-slate-300" />}
      </div>
      {show && (
        <div data-testid={`${testId}-dropdown`} className="absolute left-0 top-full z-30 mt-1 max-h-80 w-[min(360px,calc(100vw-2rem))] min-w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
          {!loading && !matchAll && (
            <div className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 backdrop-blur" data-testid={`${testId}-notfound-bar`}>
              <button type="button" data-testid={`${testId}-create`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick({ name: q.trim() })} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-[#9E1B22] hover:bg-slate-50">
                <Plus className="h-4 w-4" /> ¿No aparece? Usar “{q.trim()}” como artista nuevo
              </button>
              <div className="flex items-center gap-3 px-3 pb-2 text-[11px] text-slate-500">
                <span>Verificar en:</span>
                <a href={`https://open.spotify.com/search/${encodeURIComponent(q.trim())}/artists`} target="_blank" rel="noreferrer" onMouseDown={(e) => e.preventDefault()} data-testid={`${testId}-search-spotify`} className="inline-flex items-center gap-1 font-semibold text-[#15803d] hover:underline"><SpotifyIcon /> Spotify <ExternalLink className="h-3 w-3" /></a>
                <a href={`https://music.apple.com/search?term=${encodeURIComponent(q.trim())}`} target="_blank" rel="noreferrer" onMouseDown={(e) => e.preventDefault()} data-testid={`${testId}-search-apple`} className="inline-flex items-center gap-1 font-semibold text-black hover:underline"><AppleIcon /> Apple Music <ExternalLink className="h-3 w-3" /></a>
              </div>
            </div>
          )}
          <Group icon={Database} label="Registrados en AFM" items={res.internal} onPick={pick} prefix={`${testId}-internal`} />
          <Group icon={SpotifyIcon} label="Spotify" items={res.spotify} onPick={pick} prefix={`${testId}-spotify`} />
          <Group icon={AppleIcon} label="Apple Music" items={res.apple} onPick={pick} prefix={`${testId}-apple`} />
          {limit < 50 && (res.internal.length || res.spotify.length || res.apple.length) ? (
            <button type="button" data-testid={`${testId}-more`} onMouseDown={(e) => e.preventDefault()} onClick={() => setLimit((l) => Math.min(l + 10, 50))} disabled={loading} className="flex w-full items-center justify-center gap-2 border-t border-slate-100 px-3 py-2.5 text-xs font-semibold text-[#9E1B22] hover:bg-slate-50 disabled:opacity-50">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Ver más resultados
            </button>
          ) : null}
          {!loading && !res.internal.length && !res.spotify.length && !res.apple.length && (
            <div className="px-3 py-3 text-[11px] text-slate-500">Sin coincidencias en AFM, Spotify ni Apple Music. Pega el enlace del perfil en “Mi Perfil de Artista” si lo encuentras manualmente.</div>
          )}
        </div>
      )}
    </div>
  );
}

export const artistsToString = (arr) => (arr || []).map((a) => a.name).join(", ");
